import { useState } from "react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { errorText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FormError } from "./Fields";
import { StepTitle } from "./LoginShell";

// Second sign-in step: the 6-digit code from the authenticator app, or one recovery code.
export const MfaVerify = ({ onDone, onRestart }) => {
  const { verifyMfa } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onDone(await verifyMfa(useRecovery ? { recovery_code: value.trim() } : { code: value.trim() }));
    } catch (err) {
      if (err?.response?.status === 401 && /expired/i.test(err.response.data?.detail || "")) onRestart();
      setError(errorText(err, "That code didn't work."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-verify-form">
      <StepTitle title="Your 2-step code">
        {useRecovery ? "Type one of your recovery codes. Each one works once." : "Open your authenticator app and type the 6-digit code for Duckify."}
      </StepTitle>
      <div className="space-y-1.5">
        <Label htmlFor="mfa-code">{useRecovery ? "Recovery code" : "6-digit code"}</Label>
        <Input
          id="mfa-code"
          data-testid="mfa-code-input"
          autoFocus
          required
          autoComplete="one-time-code"
          inputMode={useRecovery ? "text" : "numeric"}
          maxLength={useRecovery ? 11 : 6}
          placeholder={useRecovery ? "abcde-fghjk" : "123456"}
          value={value}
          onChange={(e) => setValue(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ""))}
          className="h-12 rounded-full bg-white text-center font-mono2 text-xl tracking-[0.3em]"
        />
      </div>
      <FormError text={error} />
      <Button data-testid="mfa-verify-btn" type="submit" disabled={busy || (!useRecovery && value.length !== 6) || !value} className="h-12 w-full rounded-full font-bold gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Continue
      </Button>
      <button
        type="button"
        data-testid="mfa-recovery-toggle"
        onClick={() => { setUseRecovery((r) => !r); setValue(""); setError(""); }}
        className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-[#5a554b] hover:text-[#1f1f24]"
      >
        <KeyRound className="h-3.5 w-3.5" /> {useRecovery ? "Use the code from my app" : "Lost your phone? Use a recovery code"}
      </button>
    </form>
  );
};
